import React from 'react';
import type { FlowStep } from '../../types/platform';
import { cn } from '../../src/lib/utils';
type ProcessFlowProps = {
  steps: FlowStep[];
  tone?: 'light' | 'dark';
  numbered?: boolean;
  ariaLabel: string;
  /** Highlight the last step (e.g. "Institutional Memory"). */
  emphasizeLast?: boolean;
  className?: string;
};

/**
 * A process as connected stage cards: horizontal from `lg`, a simple vertical
 * stack with connectors on smaller screens.
 */
export function ProcessFlow({
  steps,
  tone = 'light',
  numbered = true,
  ariaLabel,
  emphasizeLast,
  className
}: ProcessFlowProps) {
  const dark = tone === 'dark';
  return <ol aria-label={ariaLabel} className={cn('flex flex-col lg:grid lg:gap-3', className)} style={{
    gridTemplateColumns: `repeat(${steps.length}, minmax(0, 1fr))`
  }} data-id="element-871" data-mp="3pimlqztepzkg">
      {steps.map((step, index) => {
      const last = index === steps.length - 1;
      const emphasized = emphasizeLast && last;
      return <li key={step.label} className="relative flex flex-col" data-id="element-872" data-mp="2cgxj5lkf2eq6" data-mpi={index}>
            <div className={cn('flex h-full flex-col rounded-xl border p-4', dark ? 'border-night-line bg-night-panel' : 'border-hairline bg-panel', emphasized && (dark ? 'border-violet/60 bg-violet/10' : 'border-lime/40 bg-violet-soft'))} data-id="element-873" data-mp="2ci8mxldv3mha">
              <div className="flex items-center gap-2" data-id="element-874" data-mp="2w4wtivb2jjv2">
                {numbered ? <span className={cn('font-mono text-[11px]', dark ? 'text-violet-ink' : 'text-lime')} data-id="element-875" data-mp="2w6pqfivvqppv">
                    {String(index + 1).padStart(2, '0')}
                  </span> : null}
                <span className={cn('text-[13px] font-semibold uppercase tracking-[0.06em]', dark ? 'text-night-text' : 'text-text-primary')} data-id="element-876" data-mp="2hg0daayuz0jd">
                  {step.label}
                </span>
              </div>
              {step.detail ? <p className={cn('mt-2 text-[13px] leading-relaxed', dark ? 'text-night-muted' : 'text-text-secondary')} data-id="element-877" data-mp="3a3w9lekimqfm">{step.detail}</p> : null}
              {step.items?.length ? <ul className="mt-3 flex flex-wrap gap-1.5 lg:flex-col lg:flex-nowrap" data-id="element-878" data-mp="1c7fr7zdgptm0">
                  {step.items.map(item => <li key={item} className={cn('rounded-md border px-2 py-1 text-[13px] leading-snug lg:text-xs', dark ? 'border-night-line bg-night-raised text-night-muted' : 'border-hairline-soft bg-surface text-text-secondary')} data-id="element-879" data-mp="yfhvqrcobw8b">
                      {item}
                    </li>)}
                </ul> : null}
            </div>
            {!last ? <>
                <span aria-hidden="true" className={cn('mx-auto block h-5 w-px lg:hidden', dark ? 'bg-night-line' : 'bg-hairline')} data-id="element-880" data-mp="29xj1tz7f9bnr">
                  <span className="dgpt-flow-line-v block h-full w-full" data-id="element-881" data-mp="2ethuhyzs9ftz" />
                </span>
                <span aria-hidden="true" className={cn('absolute -right-3 top-7 hidden h-px w-3 lg:block', dark ? 'bg-night-line' : 'bg-hairline')} data-id="element-882" data-mp="2oxeqv77ys0hn">
                  <span className="dgpt-flow-line block h-full w-full" data-id="element-883" data-mp="2xtxjeica9mdp" />
                </span>
              </> : null}
          </li>;
    })}
    </ol>;
}